/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Grounded in docs/agent-context/subsystems/communications.md Marketing
 * Constraints.
 *
 * CRITICAL: Email OAuth inbox sync is SHELVED pre-launch, pending
 * Google CASA Tier 2 certification and Microsoft Publisher Verification.
 * DO NOT market live inbox sync at launch.
 *
 * Honest framing for this page:
 *   - LEAD with outbound transactional emails (10 Resend templates)
 *   - LEAD with the notification system (5 wired events, preference
 *     enforcement, realtime, notification center)
 *   - Describe AI email analysis as "suggest, you review" — not
 *     autonomous triage
 *   - Put OAuth inbox sync under a "Post-Launch" section with
 *     explicit mention that production use is gated on provider cert
 *
 * Do NOT:
 *   - Promise "live Gmail / Outlook sync at launch"
 *   - Name the `email_integration` feature flag
 *   - Reveal the 10 template file names
 *   - Mention that daily digest email delivery is STUBBED
 *   - Market rules/schedule/templates/trends as polished
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Mail,
  Bell,
  Send,
  Bot,
  Link2,
  Clock,
  Briefcase,
  Calculator,
  FileText,
  DollarSign,
  Zap,
  Hourglass,
  Eye,
  Sparkles,
  Users,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Communications",
  title: "Transactional email,",
  titleHighlight: "smart notifications, and AI message review.",
  description:
    "Use launch-ready transactional emails, realtime notifications, and AI-assisted message review to keep client communication consistent and actionable.",

  featureGrid: {
    title: "Everything your client communications need on day one",
    items: [
      {
        icon: Send,
        title: "Transactional Email Suite",
        description:
          "Built-in lifecycle emails for onboarding and billing events so your team is not relying on manual follow-up.",
      },
      {
        icon: Bell,
        title: "Realtime Notification Center",
        description:
          "A notification bell that updates instantly when proposals are viewed, invoices are paid, deadlines approach, or a proposal is accepted or rejected. Your team sees what matters the moment it happens.",
      },
      {
        icon: Eye,
        title: "Proposal Engagement Tracking",
        description:
          "When a client views a proposal in the portal, you get a notification. When they accept or reject, you know right away. No more 'did they get it' calls.",
      },
      {
        icon: Clock,
        title: "Invoice & Deadline Reminders",
        description:
          "Scheduled reminders for invoices, calendar events, and deadlines so fewer follow-ups fall through the cracks.",
      },
      {
        icon: Users,
        title: "Per-User Preferences",
        description:
          "Each team member controls which notifications they receive, so the right people get the right alerts.",
      },
      {
        icon: Bot,
        title: "AI Message Review",
        description:
          "Paste or import a client email and Hank reads it. AI suggests which client, project, or estimate it belongs to, and flags potential action items. You review every suggestion before anything links.",
      },
      {
        icon: Link2,
        title: "Connect Messages to Jobs and Clients",
        description:
          "Link any imported message to a client, project, estimate, proposal, or supplier. Messages surface on the linked entity's detail page so your team has full context.",
      },
      {
        icon: Sparkles,
        title: "Message Intent Suggestions",
        description:
          "When you run AI review on a message, Hank proposes an intent — question, RFI, change request, payment inquiry — to help you triage faster. Always a suggestion, never an action.",
      },
      {
        icon: Hourglass,
        title: "Live Inbox Sync — Post-Launch",
        description:
          "Gmail and Outlook inbox sync is planned post-launch, pending required provider certification.",
      },
    ],
  },

  workflow: {
    title: "What communications look like in CC today",
    description:
      "A practical launch workflow for customer communication: send key emails, monitor events, and triage messages faster.",
    steps: [
      {
        label: "Transactional",
        title: "CC sends the emails your customers expect",
        description:
          "Cover core onboarding and billing communication automatically so clients receive timely, consistent updates.",
        details: [
          "Core account and billing lifecycle coverage",
          "Built-in reminder communication support",
          "Consistent branded messaging",
          "Launch-ready delivery foundation",
          "Reduced manual communication load",
          "Cleaner client communication cadence",
        ],
      },
      {
        label: "Notifications",
        title:
          "A realtime notification center that pays attention to your business",
        description:
          "Keep teams aware of proposal, invoice, and deadline events in real time so follow-up happens when it matters.",
        details: [
          "Proposal and invoice event awareness",
          "Deadline reminder support",
          "Realtime notification behavior",
          "Per-user preference controls",
          "Central notification center workflow",
          "Faster coordination across teams",
        ],
      },
      {
        label: "AI Review",
        title: "Import a message and let Hank read it",
        description:
          "Use AI-assisted suggestions for message triage, then confirm before any links or actions are applied.",
        details: [
          "Message paste and import support",
          "Entity-context suggestions",
          "Intent and action-item hints",
          "Human review before commit",
          "Faster communication triage",
        ],
      },
      {
        label: "Link",
        title: "Connect messages to the work they belong to",
        description:
          "Link reviewed messages to relevant records so communication history is visible where work decisions happen.",
        details: [
          "Client/project/estimate/proposal linking support",
          "Message visibility on record detail pages",
          "Searchable communication context",
          "Editable link decisions",
        ],
      },
      {
        label: "Coming Soon",
        title: "Live Gmail & Outlook inbox sync",
        description:
          "Inbox sync is on the post-launch roadmap and will be released after required provider approvals are complete.",
        details: [
          "Planned Gmail and Outlook support",
          "Certification-gated rollout",
          "Availability announced when production certification is complete",
          "Will integrate with AI review and entity-linking workflows on launch",
          "Released only after production readiness checks",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: Mail,
      title: "Ten Transactional Email Flows — Delivered at Launch",
      description:
        "Most contractor tools treat email as an afterthought. CC gives you launch-ready transactional emails for onboarding and billing moments so clients get timely, professional updates without manual chasing.",
      bullets: [
        "Welcome, onboarding complete, invoice upcoming, overdue invoice",
        "Payment failed, payment action required, subscription canceled",
        "Lifecycle and billing event coverage for the contractor-client relationship",
        "Production delivery infrastructure in place",
        "No custom mail-server setup required",
        "HTML templates with consistent branding",
      ],
    },
    {
      icon: Bell,
      title: "A Notification System That Watches Your Business For You",
      description:
        "The notification center gives your team clear visibility into key activity — proposal updates, invoice activity, and deadline reminders — so response times improve and fewer items get missed.",
      bullets: [
        "5 automated events at launch: proposal viewed / accepted / rejected, invoice paid, deadline approaching",
        "Realtime delivery for high-priority updates",
        "Per-user notification preferences",
        "Dedicated notification center UI + header bell",
        "Scheduled reminders for invoices and deadlines",
        "Expands with additional notification types over time",
      ],
    },
    {
      icon: Bot,
      title: "AI Message Review — Suggestion, Not Autopilot",
      description:
        "Paste a client email into CC or import one from a thread you care about and Hank reads it. The output is a set of suggestions — which client this probably belongs to, which project it probably references, what the intent might be, which action items might matter. Every suggestion lands in a review panel. You confirm, edit, or reject. Nothing links automatically. AI review is a triage accelerator, not a replacement for your judgment.",
      bullets: [
        "Suggested matches (client, project, estimate, proposal)",
        "Intent classification (question, RFI, change request, payment inquiry)",
        "Action item extraction",
        "Every suggestion reviewed before anything commits",
        "Built for triage acceleration, not autonomous handling",
      ],
    },
    {
      icon: Hourglass,
      title: "Live Inbox Sync — Post-Launch",
      description:
        "Inbox sync is planned for post-launch and depends on completing provider certification and production-readiness requirements.",
      bullets: [
        "Roadmapped Gmail and Outlook support",
        "Provider-certification gated availability",
        "No launch date commitment yet",
        "Rollout will follow production approval",
        "Will complement AI review workflows",
      ],
    },
  ],

  connections: {
    title: "How communications connect to the rest of CC",
    items: [
      {
        icon: FileText,
        flow: "Proposal → View Notification",
        description:
          "When a client opens, accepts, or declines a proposal, your team is notified quickly so follow-up is timely and informed.",
      },
      {
        icon: DollarSign,
        flow: "Invoice → Reminder Emails",
        description:
          "Invoice events trigger consistent reminder and status emails so customers know what needs action without manual outreach.",
      },
      {
        icon: Bell,
        flow: "Deadline → Notification",
        description:
          "Scheduled deadline reminders surface upcoming dates and expirations in one central notification workflow.",
      },
      {
        icon: Zap,
        flow: "Imported Message → AI Review → Entity Link",
        description:
          "Paste a message, Hank proposes the entity and intent, you confirm, and the message links to the client or project. Full communication history on every detail page.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Proposals & Client Portal",
      href: "/features/proposals-portal",
      icon: FileText,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    {
      label: "Project Management",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "CRM",
      href: "/features/crm",
      icon: Users,
    },
    {
      label: "AI Assistant",
      href: "/features/ai-assistant",
      icon: Bot,
    },
  ],
};

export function EmailHubPageClient() {
  return <FeaturePageTemplate config={config} />;
}
